import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { users as usersApi, posts as postsApi } from "../services/api";
import { thumbnailUrl as optimizedThumb } from "../utils/cloudinary";
import { countryFlag, countryName } from "../utils/countries";
import SimilarityBadge from "./SimilarityBadge";
import { getProfileSchema } from "../utils/seo";
import { getCredibilityLabel, getCredibilityStyle } from "../utils/cuisine";
import {
  ArrowLeft, Globe, MapPin, Image, Zap, Settings, UserX,
  UtensilsCrossed, Tent, Mountain, ThumbsUp, ThumbsDown, RotateCcw, Eye, ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const TILE_URLS = {
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
};

const CATEGORY_META = {
  food: { icon: UtensilsCrossed, color: "#f59e0b" },
  stay: { icon: Tent, color: "#8b5cf6" },
  adventure: { icon: Mountain, color: "#06d6a0" },
};

function FitBounds({ posts }) {
  const map = useMap();
  useEffect(() => {
    if (posts.length === 0) return;
    const bounds = L.latLngBounds(posts.map((p) => [p.latitude, p.longitude]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 5 });
  }, [map, posts]);
  return null;
}

function PostMap({ posts, theme, username }) {
  const postsWithCoords = posts.filter((p) => p.latitude && p.longitude);
  if (postsWithCoords.length === 0) return null;

  return (
    <div className="mx-4 mt-5 rounded-2xl overflow-hidden border border-th-border h-48">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        minZoom={1}
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
        scrollWheelZoom={false}
        doubleClickZoom={true}
        dragging={true}
      >
        <TileLayer
          url={TILE_URLS[theme]}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <FitBounds posts={postsWithCoords} />
        {postsWithCoords.map((p) => {
          const meta = CATEGORY_META[p.category] || CATEGORY_META.adventure;
          const Icon = meta.icon;
          return (
            <CircleMarker
              key={p.id}
              center={[p.latitude, p.longitude]}
              radius={7}
              pathOptions={{
                fillColor: meta.color,
                fillOpacity: 0.9,
                color: meta.color,
                weight: 2,
                opacity: 0.5,
              }}
            >
              <Popup>
                <Link to={`/profile/${username}/post/${p.id}`} className="block w-48">
                  <img
                    src={optimizedThumb(p.thumbnailUrl || p.imageUrl)}
                    alt={p.caption}
                    className="w-full h-24 object-cover rounded-t-lg"
                    loading="lazy"
                  />
                  <div className="p-2">
                    <p className="text-xs font-medium text-th-text leading-snug line-clamp-2">{p.caption}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-th-muted flex items-center gap-1 font-mono">
                        <MapPin size={8} />
                        {p.locationName}
                      </span>
                      <span className="flex items-center gap-0.5 text-[11px] font-mono" style={{ color: meta.color }}>
                        <Icon size={8} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function PostCard({ post, username }) {
  const { t } = useTranslation();
  const meta = CATEGORY_META[post.category] || CATEGORY_META.adventure;
  const Icon = meta.icon;
  return (
    <Link
      to={`/profile/${username}/post/${post.id}`}
      className="block bg-th-surface border border-th-border rounded-2xl overflow-hidden active:scale-[0.98] transition-transform"
    >
      <img src={optimizedThumb(post.thumbnailUrl || post.imageUrl)} alt={post.caption} className="w-full h-44 object-cover" loading="lazy" />
      <div className="p-3">
        <p className="text-sm text-th-text leading-snug">{post.caption}</p>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <p className="text-[11px] text-th-muted flex items-center gap-1 font-mono">
              <MapPin size={10} />
              {post.locationName}
            </p>
            {(post.takenAt || post.createdAt) && (
              <span className="text-[11px] text-th-muted font-mono">
                {new Date(post.takenAt || post.createdAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              {post.worthIt !== null && post.worthIt !== undefined && (
                <span className={`text-[11px] ${post.worthIt ? "text-emerald-500" : "text-red-400"}`}>
                  {post.worthIt ? <ThumbsUp size={9} /> : <ThumbsDown size={9} />}
                </span>
              )}
              {post.wouldReturn !== null && post.wouldReturn !== undefined && (
                <span className={`text-[11px] ${post.wouldReturn ? "text-emerald-500" : "text-amber-500"}`}>
                  <RotateCcw size={9} />
                </span>
              )}
            </div>
          </div>
          <span
            className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border ml-2"
            style={{ color: meta.color, borderColor: meta.color + "33", backgroundColor: meta.color + "11" }}
          >
            <Icon size={10} />
            {t(`categories.${post.category}`, post.category)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function LocationGroup({ location, countryCode, posts, username, expanded, onToggle, t }) {
  return (
    <div className="border border-th-border rounded-2xl overflow-hidden bg-th-surface">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-3 hover:bg-th-bg transition-colors"
      >
        {/* Preview thumbnails */}
        <div className="flex -space-x-2 flex-shrink-0">
          {posts.slice(0, 3).map((p) => (
            <img
              key={p.id}
              src={optimizedThumb(p.thumbnailUrl || p.imageUrl)}
              alt=""
              className="w-10 h-10 rounded-lg object-cover border-2 border-th-surface"
            />
          ))}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium text-th-text truncate">{countryCode ? `${countryFlag(countryCode)} ` : ""}{location}</p>
          <p className="text-[11px] text-th-muted font-mono">{t("profile.post", { count: posts.length })}</p>
        </div>
        <ChevronDown
          size={16}
          className={`text-th-muted flex-shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} username={username} />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryBar({ posts, t }) {
  const counts = useMemo(() => {
    const c = { food: 0, stay: 0, adventure: 0 };
    posts.forEach((p) => { if (c[p.category] !== undefined) c[p.category]++; });
    return c;
  }, [posts]);

  const total = posts.length || 1;

  return (
    <div className="mx-4 mt-4">
      <p className="text-[11px] font-mono uppercase tracking-wider text-th-muted mb-2">{t("profile.categorySplit")}</p>
      <div className="flex rounded-lg overflow-hidden h-2 bg-th-surface border border-th-border">
        {Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const pct = (counts[cat] / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={cat}
              style={{ width: `${pct}%`, backgroundColor: meta.color }}
              className="h-full transition-all"
            />
          );
        })}
      </div>
      <div className="flex gap-3 mt-1.5">
        {Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const Icon = meta.icon;
          return (
            <span key={cat} className="flex items-center gap-1 text-[11px] text-th-muted font-mono">
              <Icon size={10} style={{ color: meta.color }} />
              {counts[cat]}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const Profile = () => {
  const { userId: profileParam } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user: currentUser, token, isAuthenticated, loading: authLoading } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("country"); // "country" | "city"
  const [expandedGroup, setExpandedGroup] = useState(null);
  const { t } = useTranslation();

  const isOwnProfile = !profileParam || (currentUser && (profileParam === currentUser.id || profileParam === currentUser.username));

  useEffect(() => {
    // Wait for auth to finish before loading own profile
    if (!profileParam && authLoading) return;

    async function loadProfile() {
      setLoading(true);
      try {
        if (isOwnProfile && currentUser) {
          // Fetch full user data (includes explorerStats, totalImpressions)
          const [userData, data] = await Promise.all([
            usersApi.getById(currentUser.id),
            postsApi.getAll({ userId: currentUser.id }),
          ]);
          setProfileUser(userData);
          setPosts(data);
        } else if (profileParam) {
          const userData = await usersApi.getById(profileParam, token);
          const data = await postsApi.getAll({ userId: userData.id });
          setProfileUser(userData);
          setPosts(data);
        } else {
          setProfileUser(null);
          setPosts([]);
        }
      } catch {
        setProfileUser(null);
        setPosts([]);
      }
      setLoading(false);
    }
    loadProfile();
  }, [profileParam, currentUser, isOwnProfile, authLoading, token]);

  useEffect(() => {
    if (profileUser) {
      document.title = `@${profileUser.username} | Pinned`;
    }
    return () => { document.title = t("app.title"); };
  }, [profileUser, t]);

  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);

  const toggleBlock = async () => {
    if (!profileUser || blockBusy) return;
    setBlockBusy(true);
    try {
      if (profileUser.blockedByMe) {
        await usersApi.unblock(token, profileUser.id);
        setProfileUser({ ...profileUser, blockedByMe: false });
      } else {
        await usersApi.block(token, profileUser.id);
        setProfileUser({ ...profileUser, blockedByMe: true });
      }
    } catch {}
    setBlockBusy(false);
    setShowBlockConfirm(false);
  };

  useEffect(() => {
    const schema = profileUser ? getProfileSchema(profileUser) : null;
    if (!schema) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "json-ld-profile";
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [profileUser]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <p className="text-th-muted text-sm font-mono">{t("profile.loading")}</p>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="h-full flex items-center justify-center bg-th-bg">
        <p className="text-th-muted">{t("profile.userNotFound")}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
      <div className="max-w-xl mx-auto relative">
      {/* Back button */}
      {!isOwnProfile && (
        <button
          onClick={() => navigate(-1)}
          className="fixed top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-30 w-9 h-9 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center border border-white/10"
        >
          <ArrowLeft size={16} />
        </button>
      )}

      {/* Block/unblock — other users */}
      {!isOwnProfile && isAuthenticated && profileUser && (
        <div className="fixed top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-30">
          <button
            onClick={() => (profileUser.blockedByMe ? toggleBlock() : setShowBlockConfirm(true))}
            disabled={blockBusy}
            className={`h-9 px-3 rounded-xl flex items-center gap-1.5 text-xs border transition-colors disabled:opacity-50 ${
              profileUser.blockedByMe
                ? "bg-th-accent-dim border-th-accent-border text-th-accent"
                : "bg-th-surface border-th-border text-th-muted hover:text-th-text"
            }`}
          >
            <UserX size={14} />
            {profileUser.blockedByMe ? t("block.unblock") : t("block.block")}
          </button>
        </div>
      )}

      {showBlockConfirm && (
        <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-th-card border border-th-border rounded-2xl p-5 max-w-sm w-full">
            <p className="text-sm font-semibold text-th-text">
              {t("block.confirmTitle", { username: profileUser?.username })}
            </p>
            <p className="text-xs text-th-muted mt-2 leading-relaxed">{t("block.confirmBody")}</p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={toggleBlock}
                disabled={blockBusy}
                className="flex-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 py-2.5 rounded-xl disabled:opacity-50 transition-colors"
              >
                {t("block.confirm")}
              </button>
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1 text-sm font-medium text-th-muted bg-th-surface border border-th-border py-2.5 rounded-xl"
              >
                {t("block.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings — own profile only */}
      {isOwnProfile && isAuthenticated && (
        <div className="fixed top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-30">
          <button
            onClick={() => navigate("/settings")}
            className="w-9 h-9 bg-th-surface border border-th-border text-th-muted rounded-xl flex items-center justify-center hover:text-th-text transition-colors"
          >
            <Settings size={16} />
          </button>
        </div>
      )}

      {/* Header — left aligned, not centered */}
      <div className="px-4 pt-[max(1rem,env(safe-area-inset-top))] mt-2">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-th-border shadow-lg shadow-black/20 flex-shrink-0">
            {profileUser.avatarUrl ? (
              <img
                src={profileUser.avatarUrl}
                alt={profileUser.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-th-surface flex items-center justify-center text-2xl text-th-muted font-mono font-bold">
                {profileUser.username?.[0]?.toUpperCase() || "?"}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 pt-1">
            <h1 className="text-lg font-bold text-th-text truncate">@{profileUser.username}</h1>
            {(profileUser.firstName || profileUser.lastName) && (
              <p className="text-sm text-th-text-sec truncate">
                {profileUser.firstName} {profileUser.lastName}
              </p>
            )}
            {profileUser.bio && (
              <p className="text-xs text-th-muted mt-1 line-clamp-2">{profileUser.bio}</p>
            )}

            {/* Similarity — tier + shared-style evidence, inline with header */}
            {!isOwnProfile && isAuthenticated && currentUser?.travelStyles?.length > 0 && (
              <div className="mt-2">
                <SimilarityBadge viewer={currentUser} target={profileUser} align="left" />
              </div>
            )}
            {!isOwnProfile && isAuthenticated && !currentUser?.travelStyles?.length && (
              <Link
                to="/profile/edit"
                className="mt-2 inline-flex items-center gap-1.5 border border-dashed border-th-border px-3 py-1 rounded-lg text-xs text-th-muted hover:text-th-accent hover:border-th-accent-border transition-colors"
              >
                <Zap size={12} />
                {t("profile.setTravelStyle")}
              </Link>
            )}
            {!isOwnProfile && !isAuthenticated && (
              <Link to="/login" className="mt-2 inline-block text-[11px] text-th-muted hover:text-th-text-sec">
                {t("profile.loginForMatch")}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Travel Styles — horizontal scroll */}
      {profileUser.travelStyles?.length > 0 && (
        <div className="px-4 mt-4 flex flex-wrap gap-1.5">
          {profileUser.travelStyles.map((style) => (
            <span
              key={style}
              className="text-[11px] bg-th-surface border border-th-border px-2.5 py-1 rounded-lg text-th-text-sec font-mono uppercase tracking-wider"
            >
              {style}
            </span>
          ))}
        </div>
      )}

      {/* Stats strip */}
      <div className="px-4 mt-4 grid grid-cols-4 gap-2">
        <button
          onClick={() => { setGroupBy("country"); setExpandedGroup(null); }}
          aria-pressed={groupBy === "country"}
          title={t("profile.groupByCountry", "Group travel log by country")}
          className={`text-left bg-th-surface border rounded-xl px-3 py-2.5 transition-colors ${
            groupBy === "country" ? "border-th-accent ring-1 ring-th-accent" : "border-th-border"
          }`}
        >
          <Globe size={14} className={groupBy === "country" ? "text-th-accent mb-1" : "text-th-muted mb-1"} />
          <p className="text-lg font-bold text-th-text font-mono leading-none">{profileUser.explorerStats?.countries ?? profileUser.countriesVisited?.length ?? 0}</p>
          <p className="text-[11px] text-th-muted uppercase tracking-wider font-mono mt-0.5">{t("profile.countries")}</p>
        </button>
        <button
          onClick={() => { setGroupBy("city"); setExpandedGroup(null); }}
          aria-pressed={groupBy === "city"}
          title={t("profile.groupByCity", "Group travel log by city")}
          className={`text-left bg-th-surface border rounded-xl px-3 py-2.5 transition-colors ${
            groupBy === "city" ? "border-th-accent ring-1 ring-th-accent" : "border-th-border"
          }`}
        >
          <MapPin size={14} className={groupBy === "city" ? "text-th-accent mb-1" : "text-th-muted mb-1"} />
          <p className="text-lg font-bold text-th-text font-mono leading-none">{profileUser.explorerStats?.cities ?? profileUser.citiesVisited?.length ?? 0}</p>
          <p className="text-[11px] text-th-muted uppercase tracking-wider font-mono mt-0.5">{t("profile.cities")}</p>
        </button>
        <div className="bg-th-surface border border-th-border rounded-xl px-3 py-2.5">
          <Image size={14} className="text-th-muted mb-1" />
          <p className="text-lg font-bold text-th-text font-mono leading-none">{posts.length}</p>
          <p className="text-[11px] text-th-muted uppercase tracking-wider font-mono mt-0.5">{t("profile.posts")}</p>
        </div>
        <div className="bg-th-surface border border-th-border rounded-xl px-3 py-2.5">
          <Eye size={14} className="text-th-muted mb-1" />
          <p className="text-lg font-bold text-th-text font-mono leading-none">{profileUser.totalImpressions ?? 0}</p>
          <p className="text-[11px] text-th-muted uppercase tracking-wider font-mono mt-0.5">{t("profile.seen")}</p>
        </div>
      </div>

      {/* Category breakdown */}
      {posts.length > 0 && <CategoryBar posts={posts} t={t} />}

      {/* Foodie stats */}
      {profileUser.cuisineStats?.length > 0 && (
        <div className="mx-4 mt-4">
          <p className="text-[11px] font-mono uppercase tracking-wider text-th-muted mb-2">{t("profile.topCuisines")}</p>
          <div className="flex flex-wrap gap-1.5">
            {profileUser.cuisineStats.slice(0, 8).map(({ cuisine, count }) => {
              const cred = getCredibilityLabel(count);
              const style = getCredibilityStyle(cred.tier);
              return (
                <span key={cuisine} className={`inline-flex items-center gap-1 text-[11px] font-mono px-2.5 py-1 rounded-lg border capitalize ${style.color} ${style.bg} ${style.border}`}>
                  <UtensilsCrossed size={9} />
                  {cuisine} · {t(cred.labelKey)} ({count})
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Post map */}
      <PostMap posts={posts} theme={theme} username={profileUser?.username || profileParam} />

      {/* Posts — journal cards with category filter */}
      <div className="px-4 mt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-mono uppercase tracking-wider text-th-muted">
            {t("profile.travelLog")}
          </p>
          {posts.length > 0 && (
            <div className="flex gap-1">
              <button
                onClick={() => setCategoryFilter("all")}
                className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border transition-colors ${
                  categoryFilter === "all"
                    ? "bg-th-text text-th-bg border-th-text"
                    : "text-th-muted border-th-border hover:text-th-text-sec"
                }`}
              >
                {t("profile.all")}
              </button>
              {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                const Icon = meta.icon;
                const active = categoryFilter === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md border transition-colors ${
                      !active ? "text-th-muted border-th-border hover:text-th-text-sec" : ""
                    }`}
                    style={active ? {
                      color: meta.color,
                      borderColor: meta.color,
                      backgroundColor: meta.color + "18",
                    } : undefined}
                  >
                    <Icon size={10} />
                    {t(`categories.${cat}`, cat)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {(() => {
          const filtered = posts.filter((p) => categoryFilter === "all" || p.category === categoryFilter);
          if (filtered.length === 0) {
            return <p className="text-center text-sm text-th-muted py-8">{t("profile.noPostsYet")}</p>;
          }
          const groups = {};
          filtered.forEach((p) => {
            let key, code;
            if (groupBy === "country") {
              key = p.countryCode ? countryName(p.countryCode) : t("profile.other");
              code = p.countryCode || null;
            } else {
              key = p.locationName || t("profile.other");
              code = null;
            }
            if (!groups[key]) groups[key] = { posts: [], countryCode: code };
            groups[key].posts.push(p);
          });
          const sortedGroups = Object.entries(groups)
            .map(([loc, { posts: groupPosts, countryCode: cc }]) => ({
              location: loc,
              countryCode: cc,
              posts: groupPosts.sort((a, b) => new Date(b.takenAt || b.createdAt) - new Date(a.takenAt || a.createdAt)),
              newest: Math.max(...groupPosts.map((p) => new Date(p.takenAt || p.createdAt).getTime())),
            }))
            .sort((a, b) => b.newest - a.newest);
          return (
            <div className="space-y-2">
              {sortedGroups.map((g) => (
                <LocationGroup
                  key={g.location}
                  location={g.location}
                  countryCode={g.countryCode}
                  posts={g.posts}
                  username={profileUser?.username || profileParam}
                  expanded={expandedGroup === g.location}
                  onToggle={() => setExpandedGroup(expandedGroup === g.location ? null : g.location)}
                  t={t}
                />
              ))}
            </div>
          );
        })()}
      </div>

      </div>
    </div>
  );
};

export default Profile;
